import React, { useState } from 'react';

import { GDPRRdv } from 'modules/shared-gdpr/components';
import { TitleBlocContainer } from 'modules/shared-cms-advanced';
import { I18nProvider } from 'modules/shared-i18n';
import {
    AccessAppointmentForm,
    AppointmentAccess,
    GetAppointmentRefForm,
    GetAppointmentRef,
    AppointmentSummary,
    CustomerSummary,
} from 'modules/mariage/components';
import { routes } from 'config';
import { AppointmentHistory } from 'modules/mariage/components/appointment-history';
import { useRouter } from 'modules/shared/hooks';
import { Customer, Appointment } from 'modules/mariage/models';
import { alertService } from 'modules/shared-ui/components';
import { ManageAppointmentProps } from './manage-appointment-type';

import enhancer from './manage-appointment-enhancer';

import styles from './manage-appointment-page.module.css';

const innerPage: React.FC<ManageAppointmentProps> = React.memo(
    ({
        titleBlocPicture,
        sessionData,
        updateSessionData,
        getAppointment,
        getAppointments,
        getCustomer,
        deleteAppointment,
        requestRef,
        ...rest
    }: ManageAppointmentProps) => {
        // On initialise la session si elle est vide
        if (!sessionData) {
            updateSessionData({
                appointmentModification: false,
            });

            return <React.Fragment />;
        }
        const { history } = useRouter();
        const [showSummary, setShowSummary] = useState(false);
        const [noAppt, setNoAppt] = useState(false);
        const [customer, setCustomer] = useState<Customer | undefined>(undefined);
        const [apptInfo, setApptInfo] = useState<Appointment | undefined>(undefined);
        const [prevAppts, setPrevAppts] = useState<Appointment[]>([]);
        return (
            <React.Fragment>
                <div className={styles.rootContainer}>
                    {titleBlocPicture && <TitleBlocContainer titleBlocPicture={titleBlocPicture} {...rest} />}
                    {showSummary && customer && (
                        <div className={styles.customerSummary}>
                            <CustomerSummary {...customer} />
                        </div>
                    )}
                    <div className={styles.formContainer}>
                        <div className={styles.column}>
                            {showSummary && apptInfo ? (
                                <AppointmentSummary
                                    noAppointment={noAppt}
                                    onModifyButtonClick={ref => {
                                        updateSessionData({
                                            appointmentModification: true,
                                            appointmentRef: ref,
                                            customerId: customer && customer.id ? customer.id : undefined,
                                        });
                                        history.push(routes.laRobe.dressFitting.createAppointment);
                                    }}
                                    onCancelButtonClick={ref => {
                                        deleteAppointment({ id: ref }).then(() => setNoAppt(true));
                                    }}
                                    onBackButtonClick={() => {
                                        setShowSummary(false);
                                        setNoAppt(false);
                                    }}
                                    {...apptInfo}
                                />
                            ) : (
                                <AccessAppointmentForm
                                    model={new AppointmentAccess()}
                                    onSuccess={model => {
                                        getAppointment({ id: model.reference })
                                            .then(info => {
                                                const customerId = parseInt(info.customerId, 10);
                                                setApptInfo(info);

                                                return Promise.all([
                                                    getCustomer({ id: customerId }),
                                                    getAppointments({ customerId }),
                                                ]);
                                            })
                                            .then(values => {
                                                const customerInfo: Customer = values[0];
                                                const appointments: Appointment[] = values[1];

                                                setCustomer(customerInfo);

                                                if (appointments.length > 0) {
                                                    setPrevAppts(appointments);
                                                }
                                                setShowSummary(true);
                                            })
                                            .catch();
                                    }}
                                />
                            )}
                        </div>
                        <div className={styles.separator} />
                        <div className={styles.column}>
                            {showSummary ? (
                                <AppointmentHistory
                                    pastAppointments={prevAppts.map(appt => {
                                        return { ...appt };
                                    })}
                                />
                            ) : (
                                <GetAppointmentRefForm
                                    model={new GetAppointmentRef()}
                                    onSuccess={model => {
                                        requestRef(model).then(() => {
                                            alertService.show('La demande a été envoyée');
                                        });
                                    }}
                                />
                            )}
                        </div>
                    </div>

                    {showSummary && (
                        <div className={styles.gdprBloc}>
                            <I18nProvider>
                                <GDPRRdv />
                            </I18nProvider>
                        </div>
                    )}
                </div>
            </React.Fragment>
        );
    },
);

export const ManageAppointmentPage = enhancer(innerPage);
